import {
	useBlockStyle,
	useClientId,
	useCopyPasteStyles,
	useDeviceType,
} from "@blocks/hooks";
import { Swiper, SwiperSlide, SwiperClass } from "swiper/react";
import {
	A11y,
	Autoplay,
	FreeMode,
	Keyboard,
	Navigation,
	Pagination,
	Scrollbar,
	Thumbs,
} from "swiper/modules";
import { initSlider } from "./utils";
import { useSelect, withSelect } from "@wordpress/data";
import { useRefEffect } from "@wordpress/compose";
import { dateI18n, __experimentalGetSettings } from "@wordpress/date";
import { Fragment } from "@wordpress/element";
import classnames from "classnames";
import React, { useState } from "react";
import { Icon } from "../../components";
import { EditProps } from "../../types";
import "../assets/sass/blocks/slider/editor.scss";
import "../assets/sass/blocks/slider/style.scss";
import InspectorControls from "./components/InspectorControls";
import { cn } from "../../utils";
import { upperFirst } from "lodash";

interface Props extends EditProps<any> {
	categories: Array<any>;
	posts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	author: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		posts,
		className,
		attributes: {
			enablePauseOnHover,
			sliderSpeed,
			sliderStyle,
			slidesPerView,

			enableAutoPlay,
			keyNavigation,

			enableArrow,
			arrowPosition,
			arrowHorizontalPlacement,

			enableCategory,
			headerMetaPosition,

			enableMetaSeparator,
			metaSeparatorPosition,

			metaPosition,
			enableAuthor,
			enableDate,
			enableReadTime,
			enableViewCount,
			enableIcon,

			excerptLimit,
			enableExcerpt,

			enableReadMore,
			readMoreText,

			enableDot,
			hideOnDesktop,

			postTitleMarkup,
			postType,

			label,
			enableHeading,
			headingLayout,
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,

			enableViewMore,
			viewMoreText,
			enableViewMoreIcon,
			viewMoreIcon,
			viewMoreLink,
			viewButtonPosition,

			dotPosition,

			categoryPosition,
			enablePostTitleBorder,

			enablePostBoxBorder,
		},

		setAttributes,
		categories,
		tags,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "slider",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	// @ts-ignore
	const authors = useSelect((select) => {
		const { getUsers } = select("core") as any;
		return getUsers({ per_page: -1 });
	}) as any[];

	const authorOptions = authors
		? authors.map((author) => ({
				value: author?.id.toString(),
				label: author?.name,
		  }))
		: [];

	const postTypeOptions = useSelect((select) => {
		const { getPostTypes } = select("core") as any;
		const { getCurrentPost } = select("core/editor") as any;

		const allPostTypes = getPostTypes({ per_page: -1 }) || [];
		const post = getCurrentPost();
		const currentPostType = post?.type;
		const meta = post?.meta?._mzb_template;

		if (currentPostType === "mzb-builder-template") {
			return allPostTypes
				.filter((type: any) =>
					["post", "mzb-builder"].includes(type.slug)
				)
				.map((type: any) => {
					if (type.slug === "mzb-builder" && meta) {
						return {
							value: type.slug,
							label: `${upperFirst(meta)} Builder`,
						};
					}
					return {
						value: type.slug,
						label: type.name || type.slug,
					};
				});
		}

		return allPostTypes
			.filter((type: any) => type.slug !== "mzb-builder-template")
			.map((type: any) => ({
				value: type.slug,
				label: type.name || type.slug,
			}));
	}, []);

	const classNames = classnames(
		`mzb-slider mzb-slider-${clientId}`,
		className,
		hideOnDesktop && "magazine-blocks-hide-on-desktop",
		`mzb-arrow-position-${arrowPosition}`,
		`mzb-arrow-horizontal-placement--${arrowHorizontalPlacement}`
	);

	const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);

	// @ts-ignore
	const swiperRef = useRefEffect<HTMLDivElement>((element) => {
		const options = {
			slidesPerView:
				sliderStyle === "style3" || sliderStyle === "style4"
					? slidesPerView
					: 1,
			pagination: enableDot
				? {
						el: ".swiper-pagination",
						clickable: true,
				  }
				: false,
			speed: sliderSpeed ?? 500,
			loop: false,
			keyboard: {
				enabled: keyNavigation ?? false,
				onlyInViewport: true,
			},
			autoplay: enableAutoPlay
				? {
						pauseOnMouseEnter: enablePauseOnHover ?? false,
						// delay: interval ?? 5000,
						disableOnInteraction: false,
				  }
				: undefined,
			navigation: enableArrow
				? {
						nextEl: `.swiper-button-next`,
						prevEl: `.swiper-button-prev`,
				  }
				: false,
		};
		let slider = initSlider(element, options);
		return () => {
			slider.destroy();
		};
	});

	const Overlay = ({ children }: { children: React.ReactNode }) => {
		if (sliderStyle === "style2") {
			return <div className="mzb-overlay">{children}</div>;
		}
		return <>{children}</>;
	};

	const TagName = postTitleMarkup || "h3";

	const classNames3 = classnames(`mzb-post-heading mzb-${headingLayout}`, {
		[`mzb-${headingLayout1AdvancedStyle}`]:
			headingLayout === `heading-layout-1`,
		[`mzb-${headingLayout2AdvancedStyle}`]:
			headingLayout === `heading-layout-2`,
		[`mzb-${headingLayout3AdvancedStyle}`]:
			headingLayout === `heading-layout-3`,
		[`mzb-${headingLayout4AdvancedStyle}`]:
			headingLayout === `heading-layout-4`,
		[`mzb-${headingLayout5AdvancedStyle}`]:
			headingLayout === `heading-layout-5`,
		[`mzb-${headingLayout6AdvancedStyle}`]:
			headingLayout === `heading-layout-6`,
		[`mzb-${headingLayout7AdvancedStyle}`]:
			headingLayout === `heading-layout-7`,
		[`mzb-${headingLayout8AdvancedStyle}`]:
			headingLayout === `heading-layout-8`,
		[`mzb-${headingLayout9AdvancedStyle}`]:
			headingLayout === `heading-layout-9`,
	});

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
				tags={tags}
				authorOptions={authorOptions}
				postTypeOptions={postTypeOptions}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div className={classNames}>
				{(enableHeading ||
					(enableViewMore && viewButtonPosition === "top")) && (
					<div className={classNames3}>
						{enableHeading && (
							<h2 className="mzb-heading-text">{label}</h2>
						)}
						{viewButtonPosition === "top" && enableViewMore && (
							<div className="mzb-view-more">
								{/*eslint-disable-next-line react/jsx-no-target-blank */}
								<a
									href={"#"}
									target={
										viewMoreLink?.newTab
											? "_blank"
											: "_self"
									}
									rel={[
										viewMoreLink?.newTab
											? "noreferrer noopener"
											: "",
										viewMoreLink?.noFollow
											? "nofollow"
											: "",
									]
										.filter(Boolean)
										.join(" ")}
								>
									<p>{viewMoreText}</p>
									{enableViewMoreIcon && (
										<Icon
											type="frontendIcon"
											name={viewMoreIcon}
											size={24}
										/>
									)}
								</a>
							</div>
						)}
					</div>
				)}
				{arrowPosition === "top" && enableArrow && (
					<>
						<div className="swiper-button-prev"></div>
						<div className="swiper-button-next"></div>
					</>
				)}
				<div
					className="swiper"
					ref={swiperRef}
					tabIndex={0}
					role="region"
					aria-label="Image slider"
				>
					<div className="swiper-wrapper">
						{(posts || []).map((post, idx) => {
							const maxWords = excerptLimit;
							const excerpt = post?.excerpt?.rendered
								.split(" ")
								.slice(0, maxWords)
								.join(" ");

							return (
								<div
									key={idx}
									className={cn(
										sliderStyle ? sliderStyle : "style1",
										"swiper-slide",
										enablePostBoxBorder
											? "mzb-post-box-border"
											: ""
									)}
								>
									{post?.magazine_blocks_featured_image_url
										?.full?.[0] && (
										<div
											className={`mzb-featured-image ${
												categoryPosition ===
													"in-image" &&
												sliderStyle === "style3"
													? "mzb-category--inside-image"
													: ""
											}`}
										>
											<img
												src={
													post
														?.magazine_blocks_featured_image_url
														?.full[0]
												}
												alt=""
											/>
											{enableCategory &&
												categoryPosition ===
													"in-image" &&
												sliderStyle === "style3" && (
													<div className="mzb-post-meta">
														<span
															className="mzb-post-categories"
															dangerouslySetInnerHTML={{
																__html: post?.magazine_blocks_category,
															}}
														/>
													</div>
												)}
										</div>
									)}

									<Overlay>
										<div className="mzb-post-content">
											{enableCategory && (
												<div
													className={`mzb-post-meta ${
														sliderStyle ===
															"style3" &&
														headerMetaPosition ===
															"inside-image"
															? "mzb-inside-image"
															: ""
													}`}
												>
													<span
														className="mzb-post-categories"
														dangerouslySetInnerHTML={{
															__html: post?.magazine_blocks_category,
														}}
													/>
												</div>
											)}

											{metaPosition === "bottom" && (
												<>
													<TagName
														className={`mzb-post-title ${
															enablePostTitleBorder
																? "mzb-post-title-border"
																: ""
														}`}
													>
														<a href={"#"}>
															{
																post?.title
																	?.rendered
															}
														</a>
													</TagName>

													<div
														className={`mzb-post-entry-meta ${
															enableMetaSeparator &&
															metaSeparatorPosition
																? `mzb-meta-separator-${metaSeparatorPosition}`
																: ""
														}`}
													>
														{enableAuthor ===
															true && (
															<span className="mzb-post-author">
																{enableIcon ===
																	true && (
																	<img
																		className="author-display-image"
																		src={
																			post?.magazine_blocks_author_image
																		}
																	/>
																)}
																<a href={"#"}>
																	{" "}
																	{
																		post
																			?.magazine_blocks_author
																			?.display_name
																	}{" "}
																</a>
															</span>
														)}

														{enableDate ===
															true && (
															<span className="mzb-post-date">
																{enableIcon ===
																	true && (
																	<Icon
																		type="blockIcon"
																		name="calendar"
																		size={
																			24
																		}
																	/>
																)}
																<a href={"#"}>
																	{" "}
																	{dateI18n(
																		__experimentalGetSettings()
																			.formats
																			.date,
																		post?.date_gmt,
																		undefined
																	)}{" "}
																</a>
															</span>
														)}

														{enableReadTime && (
															<span className="mzb-post-read-time">
																{enableIcon ===
																	true && (
																	<Icon name="clock" />
																)}
																<span>
																	{
																		post?.magazineBlocksPostReadTime
																	}{" "}
																	min read
																</span>
															</span>
														)}
														{enableViewCount && (
															<span className="mzb-post-view-count">
																{enableIcon ===
																	true && (
																	<Icon
																		name="eyeO"
																		fill="#fff"
																	/>
																)}
																<span>
																	{post?.magazineBlocksPostViewCount ||
																		0}{" "}
																	views
																</span>
															</span>
														)}
													</div>
												</>
											)}

											{metaPosition === "top" && (
												<>
													<div
														className={`mzb-post-entry-meta ${
															enableMetaSeparator &&
															metaSeparatorPosition
																? `mzb-meta-separator-${metaSeparatorPosition}`
																: ""
														}`}
													>
														{enableAuthor ===
															true && (
															<span className="mzb-post-author">
																{enableIcon ===
																	true && (
																	<img
																		className="author-display-image"
																		src={
																			post?.magazine_blocks_author_image
																		}
																	/>
																)}
																<a href={"#"}>
																	{" "}
																	{
																		post
																			?.magazine_blocks_author
																			?.display_name
																	}{" "}
																</a>
															</span>
														)}

														{enableDate ===
															true && (
															<span className="mzb-post-date">
																{enableIcon ===
																	true && (
																	<Icon
																		type="blockIcon"
																		name="calendar"
																		size={
																			24
																		}
																	/>
																)}
																<a href={"#"}>
																	{" "}
																	{dateI18n(
																		__experimentalGetSettings()
																			.formats
																			.date,
																		post?.date_gmt,
																		undefined
																	)}{" "}
																</a>
															</span>
														)}

														{enableReadTime && (
															<span className="mzb-post-read-time">
																{enableIcon ===
																	true && (
																	<Icon name="clock" />
																)}
																<span>
																	{
																		post?.magazineBlocksPostReadTime
																	}{" "}
																	min read
																</span>
															</span>
														)}
														{enableViewCount && (
															<span className="mzb-post-view-count">
																{enableIcon ===
																	true && (
																	<Icon
																		name="eyeO"
																		fill="#fff"
																	/>
																)}
																<span>
																	{post?.magazineBlocksPostViewCount ||
																		0}{" "}
																	views
																</span>
															</span>
														)}
													</div>

													<TagName
														className={`mzb-post-title ${
															enablePostTitleBorder
																? "mzb-post-title-border"
																: ""
														}`}
													>
														<a href={"#"}>
															{
																post?.title
																	?.rendered
															}
														</a>
													</TagName>
												</>
											)}

											{(enableExcerpt ||
												enableReadMore) && (
												<div className="mzb-entry-content">
													{enableExcerpt && (
														<div
															className="mzb-entry-summary"
															dangerouslySetInnerHTML={{
																__html: excerpt,
															}}
														/>
													)}
													{enableReadMore && (
														<div className="mzb-read-more">
															<a href={"#"}>
																{readMoreText}
															</a>
														</div>
													)}
												</div>
											)}
										</div>
									</Overlay>
								</div>
							);
						})}
					</div>
					{arrowPosition === "center" &&
						enableArrow &&
						arrowHorizontalPlacement === "inside" && (
							<>
								<div className="swiper-button-prev"></div>
								<div className="swiper-button-next"></div>
							</>
						)}
					{enableDot && dotPosition === "inside-swiper" && (
						<div className="swiper-pagination"></div>
					)}
				</div>
				{arrowPosition === "bottom" && enableArrow && (
					<>
						<div className="swiper-button-prev"></div>
						<div className="swiper-button-next"></div>
					</>
				)}
				{viewButtonPosition === "bottom" && enableViewMore && (
					<div className="mzb-view-more">
						{/*eslint-disable-next-line react/jsx-no-target-blank */}
						<a
							href={"#"}
							target={viewMoreLink?.newTab ? "_blank" : "_self"}
							rel={[
								viewMoreLink?.newTab
									? "noreferrer noopener"
									: "",
								viewMoreLink?.noFollow ? "nofollow" : "",
							]
								.filter(Boolean)
								.join(" ")}
						>
							<p>{viewMoreText}</p>
							{enableViewMoreIcon && (
								<Icon
									type="frontendIcon"
									name={viewMoreIcon}
									size={24}
								/>
							)}
						</a>
					</div>
				)}
				{arrowPosition === "center" &&
					enableArrow &&
					(arrowHorizontalPlacement === "outside" ||
						arrowHorizontalPlacement === "in-between") && (
						<>
							<div className="swiper-button-prev"></div>
							<div className="swiper-button-next"></div>
						</>
					)}

				{enableDot && dotPosition === "outside-swiper" && (
					<div className="swiper-pagination swiper-pagination--outside-swiper"></div>
				)}
			</div>
		</Fragment>
	);
};

// @ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const { getCurrentPost } = select("core/editor");

	const {
		attributes: {
			category,
			tag,
			postCount,
			order,
			orderBy,
			orderType,
			authorName,
			excludedCategory,
			postType,
		},
	} = props;

	const post = getCurrentPost();
	const isTemplate = post?.type === "mzb-builder-template";
	const isArchive = post?.type === "archive";
	const isSearch = post?.type === "search";

	const selectedPostType = isTemplate ? "post" : postType || "post";

	const shouldSkipFilters = isTemplate && (isArchive || isSearch);

	const query: any = {
		per_page: postCount,
		orderby: orderBy,
		order: orderType,
		author: authorName === "all" ? undefined : authorName,
	};

	if (!shouldSkipFilters) {
		if (category && category !== "all") {
			query.categories = parseInt(category);
		}
		if (tag && tag !== "all") {
			query.tags = tag;
		}
		if (excludedCategory && excludedCategory !== "") {
			query.categories_exclude = excludedCategory;
		}
	}

	return {
		posts: getEntityRecords("postType", selectedPostType, query),
		numberOfPosts:
			getEntityRecords("postType", selectedPostType, {
				per_page: -1,
				...(shouldSkipFilters
					? {}
					: {
							categories:
								"all" === category ||
								selectedPostType !== "post"
									? undefined
									: parseInt(category),
					  }),
			})?.length || 0,
		categories:
			getEntityRecords("taxonomy", "category", { per_page: -1 }) || [],
		tags: getEntityRecords("taxonomy", "post_tag", { per_page: -1 }) || [],
	};
})(Edit);
